import React, { useState, useEffect, forwardRef } from 'react';
import { Search, Loader2, Crown } from 'lucide-react';
import { debounce } from 'lodash-es';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { clientsApi } from '../../api/clientsApi';
import { isPro as checkIsPro } from '../../utils/planPermissions';

// Client search result item interface - EXPORTADO para uso externo
export interface SearchResultItem {
  id: string;
  name: string | null;
  phone: string;
  countryCode?: string;
  points?: number;
}

interface SearchInputProps {
  onSearch: (term: string | null) => void; 
  onClientSelect?: (client: SearchResultItem) => void;
  placeholder?: string;
  initialValue?: string;
  className?: string;
  debounceTime?: number;
  minSearchLength?: number;
  disabled?: boolean;
  showMinLengthWarning?: boolean;
  showResults?: boolean;
  respectEmptyPlaceholder?: boolean;
  preventClientNavigation?: boolean;
  /**
   * Salta el gate PRO. Para pantallas donde el buscador NO es una comodidad sino el ÚNICO
   * selector de cliente y sin él la pantalla no sirve (Canjear Recompensas). Canjear ya es
   * libre en el kiosco (app.lealify.com no gatea el canje), así que bloquearlo aquí no
   * defendía ingresos: solo hacía ver rota una pantalla que el menú ofrece a todos.
   */
  sinGatePro?: boolean;
  /**
   * Cómo se avisa del gate a quien no es PRO:
   *   'inline'  (default) línea fija bajo el campo — se ve también en móvil.
   *   'tooltip' solo al pasar el mouse — para el buscador del encabezado, donde una
   *             línea extra descuadra la barra.
   */
  avisoPro?: 'inline' | 'tooltip';
}

const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(
  ({ 
    onSearch, 
    onClientSelect,
    placeholder = "Buscar cliente por nombre o teléfono", 
    initialValue = '', 
    className = '',
    debounceTime = 400,
    minSearchLength = 2,
    disabled = false,
    showMinLengthWarning = true,
    showResults = true,
    respectEmptyPlaceholder = false,
    preventClientNavigation = false,
    sinGatePro = false,
    avisoPro = 'inline'
  }, ref) => {
    const [searchInput, setSearchInput] = useState(initialValue);
    const [isLoading, setIsLoading] = useState(false);
    const [searchResults, setSearchResults] = useState<SearchResultItem[]>([]);
    const [showResultsDropdown, setShowResultsDropdown] = useState(false);
    const { currentUser } = useAuth();
    const navigate = useNavigate();

    // ¿Tiene derecho a buscar? `sinGatePro` lo concede a la pantalla que no puede operar
    // sin el buscador (ver la prop). Se mantiene el nombre para no tocar el resto del archivo.
    const hasProAccess = sinGatePro || checkIsPro(currentUser?.subscription);

    // Search for clients directly
    const performSearch = async (term: string) => {
      if (!term || term.length < minSearchLength || !currentUser?.businessId) {
        setSearchResults([]);
        setShowResultsDropdown(false);
        return;
      }

      try {
        setIsLoading(true);

        const { clients } = await clientsApi.list({ q: term, size: 5 });

        const mappedResults: SearchResultItem[] = clients.map((c) => ({
          id: c.id,
          name: c.visitorName || null,
          phone: c.phoneNumber || '',
          countryCode: c.countryCode || '',
          points: c.points || 0,
        }));

        setSearchResults(mappedResults);
        setShowResultsDropdown(showResults && mappedResults.length > 0);
      } catch (error) {
        console.error('Error searching for clients:', error);
        setSearchResults([]);
      } finally {
        setIsLoading(false);
      }
    };

    // Create debounced search function
    const debouncedSearch = React.useMemo(
      () => debounce(performSearch, debounceTime),
      [currentUser?.businessId, debounceTime, minSearchLength]
    );

    // Manage search input changes
    const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!hasProAccess) {
        setSearchInput("");
        debouncedSearch.cancel();
        setIsLoading(false);
        setSearchResults([]);
        setShowResultsDropdown(false);
        return;
      }
      
      const term = e.target.value;
      setSearchInput(term);
      
      if (term.length === 0) {
        debouncedSearch.cancel();
        setSearchResults([]);
        setShowResultsDropdown(false);
        onSearch(null);
        setIsLoading(false);
      } else if (term.length >= minSearchLength) {
        setIsLoading(true);
        debouncedSearch(term);
      } else {
        debouncedSearch.cancel();
        setSearchResults([]);
        setShowResultsDropdown(false);
        setIsLoading(false);
      }
    };

    // Handle result click
    const handleResultClick = (clientId: string, result: SearchResultItem) => {
      setSearchInput('');
      setSearchResults([]);
      setShowResultsDropdown(false);

      if (onClientSelect) {
        onClientSelect(result);
      } else {
        onSearch(clientId);
      }

      if (!preventClientNavigation) {
        navigate(`/dashboard/clients/${clientId}/history`);
      }
    };

    // Handle input focus to potentially show dropdown if there are results
    const handleInputFocus = () => {
      if (searchResults.length > 0 && showResults) {
        setShowResultsDropdown(true);
      }
    };

    // Handle upgrade click
    const handleUpgradeClick = () => {
      navigate('/dashboard/subscription');
    };

    // Format phone number for display (basic format)
    const formatPhoneDisplay = (countryCode: string = '', phone: string = '') => {
      return `${countryCode} ${phone}`.trim();
    };

    // Handle document click to close dropdown
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (showResultsDropdown && !(event.target as Element).closest('.search-container')) {
          setShowResultsDropdown(false);
        }
      };
      
      document.addEventListener('click', handleClickOutside);
      return () => {
        document.removeEventListener('click', handleClickOutside);
      };
    }, [showResultsDropdown]);

    // Clear debounced function on unmount
    useEffect(() => {
      return () => {
        debouncedSearch.cancel();
      };
    }, [debouncedSearch]);

    // Set the initial value if provided and changed
    useEffect(() => {
      if (initialValue !== searchInput) {
        setSearchInput(initialValue);
        if (initialValue && initialValue.length >= minSearchLength) {
          debouncedSearch(initialValue);
        } else {
          setSearchResults([]);
          setShowResultsDropdown(false);
        }
      }
    }, [initialValue, minSearchLength, debouncedSearch]);

    // Determine what placeholder to show
    const effectivePlaceholder = () => {
      if (respectEmptyPlaceholder && placeholder === "") {
        return "";
      }
      return hasProAccess ? placeholder : "";
    };

    return (
      <div className={`relative search-container ${className}`}>
        <div className="relative group">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            {isLoading ? (
              <Loader2 className="h-5 w-5 text-blue-500 animate-spin" aria-hidden="true" />
            ) : (
              <Search className={`h-5 w-5 ${hasProAccess ? 'text-content-3' : 'text-content-3'}`} aria-hidden="true" />
            )}
          </div>
          
          <input
            type="text"
            ref={ref}
            value={hasProAccess ? searchInput : ""}
            onChange={handleSearchInputChange}
            onFocus={handleInputFocus}
            disabled={disabled || !hasProAccess}
            className={`focus:ring-accent focus:border-blue-500 block w-full pl-10 py-2 sm:text-sm border-line rounded-md 
              ${!hasProAccess ? 'bg-surface-2 text-content-3 cursor-not-allowed' : 'disabled:opacity-50 disabled:bg-canvas'}`}
            placeholder={effectivePlaceholder()}
            aria-label="Search"
          />

          {!hasProAccess && (
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
              <button
                onClick={handleUpgradeClick}
                className="inline-flex items-center text-xs text-yellow-700 dark:text-yellow-300 hover:text-yellow-800 dark:text-yellow-300"
              >
                <Crown className="h-3.5 w-3.5 mr-0.5 text-yellow-500" />
                <span>Activar PRO</span>
              </button>
            </div>
          )}
          
          {/* Aviso del gate en el encabezado: tooltip (una línea fija ahí descuadra la barra) */}
          {!hasProAccess && avisoPro === 'tooltip' && (
            <div className="absolute left-0 right-0 -bottom-8 hidden group-hover:block text-xs font-medium text-yellow-700 dark:text-yellow-300 bg-yellow-50 dark:bg-yellow-500/10 px-2 py-1 rounded border border-yellow-200 dark:border-yellow-500/30 whitespace-nowrap z-10 shadow-sm">
              <Crown className="h-2.5 w-2.5 inline mr-0.5 text-yellow-500" />
              Buscar clientes es del plan PRO
            </div>
          )}

          {/* Search Results Dropdown */}
          {hasProAccess && showResultsDropdown && searchResults.length > 0 && (
            <div className="absolute z-50 mt-1 w-full bg-surface shadow-lg rounded-md border border-line overflow-hidden max-h-72 overflow-y-auto">
              <ul className="divide-y divide-line">
                {searchResults.map(result => (
                  <li 
                    key={result.id}
                    className="cursor-pointer hover:bg-surface-2"
                    onClick={() => handleResultClick(result.id, result)}
                  >
                    <div className="px-4 py-3">
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="text-sm font-medium text-content">{result.name || 'Sin nombre'}</p>
                          <p className="text-sm text-content-3">{formatPhoneDisplay(result.countryCode, result.phone)}</p>
                        </div>
                        {result.points !== undefined && (
                          <div className="flex items-center text-sm text-yellow-600 dark:text-yellow-400 font-medium ml-4 flex-shrink-0">
                            <span className="inline-block w-4 h-4 text-center text-yellow-500 mr-1">★</span>
                            {result.points}
                          </div>
                        )}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
              <div className="px-4 py-2 bg-surface-2 border-t border-line">
                <p className="text-xs text-content-3 text-center">
                  Haz clic en un resultado para seleccionar al cliente
                </p>
              </div>
            </div>
          )}
          
          {/* Message when no results are found */}
          {hasProAccess && showResultsDropdown && searchResults.length === 0 && searchInput.length >= minSearchLength && !isLoading && (
            <div className="absolute z-50 mt-1 w-full bg-surface shadow-lg rounded-md border border-line px-4 py-3 text-sm text-content-3 text-center">
              No se encontraron clientes.
            </div>
          )}
        </div>
        
        {hasProAccess && showMinLengthWarning && searchInput.length > 0 && searchInput.length < minSearchLength && (
          <div className="text-xs text-content-3 mt-1 pl-1">
            Escribe al menos {minSearchLength} caracteres para buscar
          </div>
        )}

        {/* Aviso del gate, VISIBLE también en móvil (el tooltip de hover no existe en un
            teléfono: el cajero solo veía un campo gris que no aceptaba teclas). Dice qué se
            gana y qué sigue disponible, no solo qué está bloqueado. */}
        {!hasProAccess && avisoPro === 'inline' && (
          <div className="mt-1.5 flex items-start gap-1.5 text-xs text-content-2">
            <Crown className="h-3.5 w-3.5 mt-0.5 flex-shrink-0 text-yellow-500" />
            <span>
              Buscar por nombre o teléfono es del plan PRO.{' '}
              <span className="text-content-3">Tu lista de clientes completa sigue disponible.</span>
            </span>
          </div>
        )}
      </div>
    );
  }
);

SearchInput.displayName = 'SearchInput';

export default SearchInput;